<template>
	<div class="crRoot">
		<nav class="tabs" aria-label="Code review pages">
			<router-link :to="{ name: 'codeReviewQueue' }">Findings</router-link>
			<router-link :to="{ name: 'codeReviewPushes' }">Reviewed pushes</router-link>
			<router-link :to="{ name: 'codeReviewFixes' }">Fixes</router-link>
			<router-link :to="{ name: 'codeReviewReport' }">Report</router-link>
		</nav>

		<div v-if="error" class="errorBox">{{error}}</div>
		<div v-if="loading && !data" class="loadingInline"><ScaleLoader /> Loading…</div>

		<template v-if="data">
			<h1 class="pageHeading"><span class="mono">{{b.displayId}}</span> {{b.repo}} fix batch</h1>
			<div class="row">
				<span class="badge" :class="statusClass(b.status)">{{label(b.status)}}</span>
				<span class="hintText">{{b.trigger}}, requested by {{b.requestedBy}} {{formatTime(b.createdAt)}}</span>
				<span v-if="polling" class="loadingInline" aria-hidden="true"><ScaleLoader /></span>
			</div>
			<div v-if="b.statusReason" class="preText">{{b.statusReason}}</div>
			<div v-if="!data.fixesEnabled && b.status === 'Queued'" class="warningBox">Autonomous fixes are turned off (setting codeReviewFixesEnabled), so this batch waits.</div>

			<!-- Actions -->
			<div v-if="b.canPush || b.canRetryPush || b.canDiscard" class="card actions">
				<div class="row">
					<template v-if="data.canApprove">
						<slide-button v-if="b.canPush || b.canRetryPush"
									  key="slide_push"
									  class="crSlide"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="260"
									  :height="30"
									  :color="slideColors.positive"
									  :text="b.canPush ? 'slide to approve and push' : 'slide to retry the push'"
									  success-text="pushing…"
									  :disabled="busy || !data.fixesEnabled"
									  @completed="push" />
						<slide-button v-if="b.canDiscard"
									  key="slide_discard"
									  class="crSlide"
									  :auto-width="false"
									  :circle="true"
									  :noanimate="true"
									  :width="200"
									  :height="30"
									  :color="slideColors.danger"
									  text="slide to discard"
									  success-text="discarding…"
									  :disabled="busy"
									  @completed="discard" />
					</template>
					<span v-if="busy" class="loadingInline"><ScaleLoader /> Working…</span>
					<span v-if="!data.canApprove" class="hintText">Approving or discarding needs the Code Review Approve Fixes permission.</span>
					<span v-else-if="(b.canPush || b.canRetryPush) && !data.fixesEnabled" class="hintText">Pushing is disabled while autonomous fixes are turned off.</span>
				</div>
				<div v-if="data.canApprove && b.canDiscard" class="hintText">Discarding pushes nothing: the agent deletes its fix branch, and the findings go back to Open.</div>
				<div v-if="b.canPush || b.canRetryPush" class="hintText">
					<template v-if="b.uncommittedChanges.length || b.warnings.length"><b>Check the warnings below first.</b> </template>Pushes exactly <span class="mono">{{shortSha(b.fixHeadSha)}}</span> ({{b.commitCount}} commit{{b.commitCount === 1 ? '' : 's'}}) to {{b.baseBranch}}. The agent rebases onto the current {{b.baseBranch}} first and refuses the push on a conflict or when the commits change.
				</div>
				<div v-if="actionError" class="errorBox" role="alert">{{actionError}}</div>
				<div v-if="actionInfo" class="hintText" role="status">{{actionInfo}}</div>
			</div>

			<!-- Warnings -->
			<div v-if="b.uncommittedChanges.length" class="warningBox" role="note">
				<b>The agent left {{b.uncommittedChanges.length}} uncommitted change{{b.uncommittedChanges.length === 1 ? '' : 's'}}.</b> They are never pushed; check whether the fix is complete.
				<ul class="compactList mono"><li v-for="(u, i) in b.uncommittedChanges" :key="i">{{u}}</li></ul>
			</div>
			<div v-if="b.warnings.length" class="warningBox" role="note">
				<b>Cross-check warnings</b>
				<ul class="compactList"><li v-for="(w, i) in b.warnings" :key="i">{{w}}</li></ul>
			</div>
			<div v-if="b.build && b.build.succeeded === false" class="warningBox" role="note"><b>The agent reports that its build or tests failed.</b> {{b.build.details}}</div>

			<!-- Findings -->
			<section class="card tableCard" aria-labelledby="crbFindingsHeading">
				<h2 id="crbFindingsHeading" class="secondaryHeading">Findings</h2>
				<table class="dense">
					<thead>
						<tr>
							<th scope="col">ID</th>
							<th scope="col">Severity</th>
							<th scope="col">Title</th>
							<th scope="col">Outcome</th>
							<th scope="col">Commit</th>
							<th scope="col">Note</th>
							<th scope="col">Status now</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="item in b.findings" :key="item.id">
							<td class="nowrap"><router-link :to="{ name: 'codeReviewFinding', params: { id: item.id } }">CR-{{item.id}}</router-link></td>
							<td><span v-if="item.finding" class="badge" :class="severityClass(item.finding.effectiveSeverity)">{{item.finding.effectiveSeverity}}</span></td>
							<td class="titleCell">{{item.finding ? item.finding.title : '(deleted)'}}<div v-if="item.finding" class="hintText mono">{{location(item.finding)}}</div></td>
							<td class="nowrap"><span v-if="item.outcome" class="badge" :class="statusClass(item.outcome)">{{label(item.outcome)}}</span><div v-if="item.reportedOutcome" class="hintText">reported {{label(item.reportedOutcome)}}</div></td>
							<td class="mono nowrap">{{shortSha(item.commitSha)}}</td>
							<td class="preText noteCell">{{item.note}}</td>
							<td><span v-if="item.finding" class="badge" :class="statusClass(item.finding.status)">{{label(item.finding.status)}}</span></td>
						</tr>
					</tbody>
				</table>
			</section>

			<div class="twoCol">
				<!-- Job -->
				<section class="card" aria-labelledby="crbJobHeading">
					<h2 id="crbJobHeading" class="secondaryHeading">Job</h2>
					<table class="infoTable">
						<tbody>
							<tr><th scope="row">Base</th><td>{{b.baseBranch || '—'}} <a v-if="b.baseShaUrl" :href="b.baseShaUrl" target="_blank" rel="noopener" class="mono">{{shortSha(b.baseSha)}}</a></td></tr>
							<tr><th scope="row">Fix head</th><td class="mono">{{b.fixHeadSha || '—'}}</td></tr>
							<tr><th scope="row">Agent job</th><td class="mono">{{b.agentJobId || '—'}}</td></tr>
							<tr><th scope="row">Started</th><td>{{formatTime(b.startedAt)}}<template v-if="b.jobEndedAt">, ended {{formatTime(b.jobEndedAt)}}</template></td></tr>
							<tr v-if="b.retryAfter"><th scope="row">Retry after</th><td>{{formatTime(b.retryAfter)}}</td></tr>
							<tr v-for="r in b.runs" :key="r.id"><th scope="row">Run #{{r.id}}</th><td><span class="badge" :class="statusClass(r.status)">{{label(r.status)}}</span> {{r.model}} · {{formatCost(r.costUsd)}} · {{formatDurationMs(r.durationMs)}}<div v-if="r.statusReasons.length" class="hintText">{{r.statusReasons.join('; ')}}</div></td></tr>
						</tbody>
					</table>
				</section>

				<!-- Build and summary -->
				<section class="card" aria-labelledby="crbBuildHeading">
					<h2 id="crbBuildHeading" class="secondaryHeading">Build and summary</h2>
					<div v-if="b.build">
						Build: <b>{{!b.build.attempted ? 'not attempted' : (b.build.succeeded === true ? 'succeeded' : (b.build.succeeded === false ? 'failed' : 'unknown'))}}</b>
						<div class="preText hintText">{{b.build.details}}</div>
					</div>
					<div v-else class="hintText">No report yet.</div>
					<h3 class="subHeading">Agent summary</h3>
					<div class="preText">{{b.summary || '(none)'}}</div>
				</section>
			</div>

			<!-- Commits and diff -->
			<section class="card" aria-labelledby="crbCommitsHeading">
				<h2 id="crbCommitsHeading" class="secondaryHeading">Commits ({{b.commits.length}})</h2>
				<div v-if="!b.commits.length" class="hintText">No commits.</div>
				<div v-for="c in b.commits" :key="c.sha" class="commit">
					<div><span class="mono">{{shortSha(c.sha)}}</span> <b>{{c.subject}}</b> <span v-if="c.author" class="hintText">({{c.author}})</span></div>
					<div v-if="commitBody(c)" class="preText hintText">{{commitBody(c)}}</div>
				</div>
				<div v-if="b.commits.length" class="hintText">SHAs are those of the agent's fix branch; the push rebases the commits, so the pushed SHAs differ<template v-if="b.pushedSha"> (pushed head <a v-if="b.pushedShaUrl" :href="b.pushedShaUrl" target="_blank" rel="noopener" class="mono">{{shortSha(b.pushedSha)}}</a>)</template>.</div>
				<template v-if="b.diffStat">
					<h3 class="subHeading">Diff stat</h3>
					<pre class="scrollPre">{{b.diffStat}}</pre>
				</template>
				<template v-if="b.commits.length">
					<h3 class="subHeading">Full diff<span v-if="b.diffTruncated" class="hintText"> (truncated by the agent; {{formatCount(b.diffBytes)}} bytes in all)</span></h3>
					<div v-if="diff.loading" class="loadingInline"><ScaleLoader /> Loading the diff…</div>
					<div v-else-if="diff.error" class="errorBox">{{diff.error}}</div>
					<template v-else-if="diff.loaded">
						<div v-if="!diff.text" class="hintText">No diff is stored for this batch.</div>
						<pre v-else class="diffPre" tabindex="0" aria-label="Full diff"><span v-for="(line, i) in visibleDiffLines" :key="i" :class="lineClass(line)">{{line}}
</span></pre>
						<button type="button" v-if="diffLines.length > visibleDiffLines.length" class="linkButton" @click="showAllDiff = true">Show all {{formatCount(diffLines.length)}} lines</button>
					</template>
				</template>
				<template v-if="diff.uncommitted">
					<h3 class="subHeading">Uncommitted changes (not pushed)</h3>
					<pre class="diffPre"><span v-for="(line, i) in uncommittedLines" :key="i" :class="lineClass(line)">{{line}}
</span></pre>
				</template>
			</section>

			<!-- Push -->
			<section v-if="b.pushAttempts || b.pushLog || b.pushError" class="card" aria-labelledby="crbPushHeading">
				<h2 id="crbPushHeading" class="secondaryHeading">Push</h2>
				<table class="infoTable">
					<tbody>
						<tr><th scope="row">Attempts</th><td>{{b.pushAttempts}}</td></tr>
						<tr v-if="b.approvedBy"><th scope="row">Approved</th><td>{{b.approvedBy}}, {{formatTime(b.approvedAt)}}</td></tr>
						<tr v-if="b.pushedAt"><th scope="row">Pushed</th><td>{{formatTime(b.pushedAt)}} <a v-if="b.pushedShaUrl" :href="b.pushedShaUrl" target="_blank" rel="noopener" class="mono">{{shortSha(b.pushedSha)}}</a></td></tr>
						<tr v-if="b.pushError"><th scope="row">Error</th><td class="errorText">{{b.pushError}}</td></tr>
					</tbody>
				</table>
				<template v-if="b.pushLog">
					<h3 class="subHeading">Push log</h3>
					<pre class="scrollPre">{{b.pushLog}}</pre>
				</template>
			</section>
		</template>
	</div>
</template>

<script>
	import { GetFixBatch, GetFixBatchDiff, PushFixBatch, DiscardFixBatch } from 'appRoot/api/CodeReviewData';
	import SlideButton from 'appRoot/vues/common/controls/SlideButton.vue';
	import { label, severityClass, statusClass, formatTime, formatCost, formatCount, formatDurationMs, location, shortSha, slideColors } from './codeReviewFormat';

	/** How often the page checks a batch that is queued, running or pushing. */
	const pollMs = 5000;
	/** Lines of the diff shown before "Show all". */
	const diffLineLimit = 20000;

	function splitLines(text)
	{
		if (!text)
			return [];
		let lines = text.replace(/\r\n/g, '\n').split('\n');
		if (lines.length && lines[lines.length - 1] === '')
			lines.pop();
		return lines;
	}

	export default {
		props:
		{
			batchId: {
				type: Number,
				required: true
			}
		},
		components: { SlideButton },
		data()
		{
			return {
				slideColors,
				data: null,
				loading: false,
				error: null,
				loadToken: 0,
				busy: false,
				actionError: null,
				actionInfo: null,
				timer: null,
				diff: {},
				showAllDiff: false
			};
		},
		created()
		{
			this.load();
		},
		beforeUnmount()
		{
			clearTimeout(this.timer);
		},
		watch:
		{
			batchId()
			{
				clearTimeout(this.timer);
				this.data = null;
				this.diff = {};
				this.showAllDiff = false;
				this.actionError = null;
				this.actionInfo = null;
				this.load();
			}
		},
		computed:
		{
			b()
			{
				return this.data ? this.data.batch : {};
			},
			polling()
			{
				return !!this.data && (this.b.status === 'Queued' || this.b.status === 'Running' || this.b.status === 'Pushing');
			},
			diffLines()
			{
				return splitLines(this.diff.text);
			},
			visibleDiffLines()
			{
				return this.showAllDiff ? this.diffLines : this.diffLines.slice(0, diffLineLimit);
			},
			uncommittedLines()
			{
				return splitLines(this.diff.uncommitted);
			}
		},
		methods:
		{
			label,
			severityClass,
			statusClass,
			formatTime,
			formatCost,
			formatCount,
			formatDurationMs,
			location,
			shortSha,
			commitBody(c)
			{
				if (!c.message)
					return '';
				let nl = c.message.indexOf('\n');
				return nl < 0 ? '' : c.message.substr(nl + 1).trim();
			},
			lineClass(line)
			{
				if (line.startsWith('diff --git') || line.startsWith('index ') || line.startsWith('+++') || line.startsWith('---'))
					return 'dFile';
				if (line.startsWith('@@'))
					return 'dHunk';
				if (line.startsWith('+'))
					return 'dAdd';
				if (line.startsWith('-'))
					return 'dDel';
				return null;
			},
			async load()
			{
				if (isNaN(this.batchId))
				{
					this.error = 'Invalid batch ID.';
					return;
				}
				clearTimeout(this.timer);
				let token = ++this.loadToken;
				this.loading = true;
				this.error = null;
				try
				{
					let data = await GetFixBatch(this.batchId);
					if (token !== this.loadToken)
						return;
					if (data.success)
					{
						this.data = data;
						document.title = data.batch.displayId + ' ' + data.batch.repo + ' - ' + appContext.systemName;
						if (data.batch.commits.length && (!this.diff.loaded || this.diff.fixHeadSha !== data.batch.fixHeadSha) && !this.diff.loading)
							this.loadDiff();
					}
					else
						this.error = data.error;
				}
				catch (ex)
				{
					if (token === this.loadToken)
						this.error = ex.message;
				}
				finally
				{
					if (token === this.loadToken)
					{
						this.loading = false;
						if (this.polling)
							this.timer = setTimeout(() => this.load(), pollMs);
					}
				}
			},
			async loadDiff()
			{
				let id = this.batchId;
				this.diff = { loading: true };
				let entry;
				try
				{
					let r = await GetFixBatchDiff(id);
					entry = r.success ? { loaded: true, text: r.diff, uncommitted: r.uncommittedDiff, fixHeadSha: r.fixHeadSha } : { error: r.error };
				}
				catch (ex)
				{
					entry = { error: ex.message };
				}
				if (id === this.batchId)
					this.diff = entry;
			},
			async runAction(fn, success)
			{
				if (this.busy)
					return;
				this.busy = true;
				this.actionError = null;
				this.actionInfo = null;
				try
				{
					let response = await fn();
					if (!response.success)
						this.actionError = response.error;
					else
						this.actionInfo = success;
					await this.load();
				}
				catch (ex)
				{
					this.actionError = ex.message;
				}
				finally
				{
					this.busy = false;
				}
			},
			// The slide buttons stand in for confirmation dialogs; the page states what each does next to them.
			async push()
			{
				let head = this.b.fixHeadSha;
				await this.runAction(() => PushFixBatch(this.b.id, head), 'Push requested; the agent pushes between jobs.  This page checks every few seconds.');
			},
			async discard()
			{
				await this.runAction(() => DiscardFixBatch(this.b.id), 'Discarded.');
			}
		}
	};
</script>

<style scoped src="./codeReview.css"></style>
<style scoped>
	.tableCard
	{
		overflow-x: auto;
	}

	.twoCol
	{
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
	}

		.twoCol > .card
		{
			flex: 1 1 380px;
			min-width: 0;
		}

	.subHeading
	{
		font-size: 14px;
		font-weight: bold;
		margin: 10px 0 4px 0;
	}

	.compactList
	{
		margin: 4px 0;
		padding-left: 20px;
	}

	.commit
	{
		margin: 4px 0 8px 0;
	}

	.noteCell
	{
		min-width: 240px;
		max-width: 520px;
	}

	.errorText
	{
		color: #A00000;
	}

	button.primary
	{
		font-weight: bold;
	}

	.diffPre
	{
		max-height: 600px;
		overflow: auto;
		background-color: #FAFAFA;
		border: 1px solid #DDDDDD;
		padding: 6px 0;
		font-family: Consolas, monospace;
		font-size: 12px;
		margin: 4px 0;
		white-space: pre;
	}

		.diffPre > span
		{
			display: block;
			box-sizing: border-box;
			width: max-content;
			min-width: 100%;
			padding: 0 8px;
			min-height: 1.2em;
		}

	/* Diff line colors: text on each background has a contrast ratio of at least 4.5:1. */
	.dAdd
	{
		background-color: #E6FFEC;
		color: #0A3D1A;
	}

	.dDel
	{
		background-color: #FFEBE9;
		color: #67060C;
	}

	.dHunk
	{
		background-color: #DDF4FF;
		color: #0A3069;
	}

	.dFile
	{
		background-color: #EEEEEE;
		color: #222222;
		font-weight: bold;
	}
</style>
